import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Button, FormField, Header, Input, Modal, Select, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { AssociateAccountRequest } from 'aws-sdk/clients/omnismusservice';
import { associateAccount } from 'src/api/omnismus';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { AccountEntry } from '../utils/smusUtil';

export interface GroupOption {
  teamId: string;
  groupName: string;
}

export interface AssociateAccountFormProps {
  visible: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  groups: GroupOption[];
  editAccount?: AccountEntry;
}

export const AssociateAccountForm = (props: AssociateAccountFormProps) => {
  const isEditMode = !!props.editAccount;
  const isSingleGroup = props.groups.length === 1;

  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<OptionDefinition | null>(null);
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');

  const groupOptions: OptionDefinition[] = props.groups.map((group) => ({
    label: group.groupName,
    value: group.teamId,
  }));

  useEffect(() => {
    if (!props.visible) return;

    if (props.editAccount) {
      const matchingOption = groupOptions.find((option) => option.value === props.editAccount!.teamId) ?? null;
      setSelectedGroup(matchingOption);
      setAccountId(props.editAccount.accountId);
      setAccountName(props.editAccount.name ?? '');
    } else {
      // Create mode
      setSelectedGroup(isSingleGroup ? groupOptions[0] : null);
      setAccountId('');
      setAccountName('');
    }
  }, [props.visible, props.editAccount, props.groups]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const teamId = selectedGroup?.value ?? '';
      const request: AssociateAccountRequest = { teamId, accountId, accountName };
      await associateAccount(request);
      props.onSuccess();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  };

  const isSubmitDisabled = !selectedGroup || !accountId || !accountName;

  return (
    <Modal
      visible={props.visible}
      onDismiss={props.onDismiss}
      header={<Header variant='h2'>{isEditMode ? 'Edit account' : 'Associate account'}</Header>}
      footer={
        <Box float='right'>
          <SpaceBetween size='xs' direction='horizontal'>
            <Button variant='link' onClick={props.onDismiss}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleSubmit} loading={loading} disabled={isSubmitDisabled}>
              {isEditMode ? 'Save' : 'Associate'}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='medium'
    >
      <form style={{ width: '100%' }}>
        <SpaceBetween size='l'>
          <FormField
            label='SSO group'
            description='The SSO group this account will be associated with.'
            constraintText='Required'
            stretch={true}
          >
            <Select
              selectedOption={selectedGroup}
              onChange={({ detail }) => setSelectedGroup(detail.selectedOption)}
              options={groupOptions}
              placeholder='Select an SSO group'
              disabled={isSingleGroup || isEditMode}
              selectedAriaLabel='Selected'
            />
          </FormField>
          <FormField
            label='Account ID'
            description='The AWS account ID to associate.'
            constraintText='Required'
            stretch={true}
          >
            <Input
              value={accountId}
              onChange={({ detail }) => setAccountId(detail.value)}
              placeholder='123456789012'
              disabled={isEditMode}
            />
          </FormField>
          <FormField
            label='Account name'
            description='The display name for the account.'
            constraintText='Required'
            stretch={true}
          >
            <Input
              value={accountName}
              onChange={({ detail }) => setAccountName(detail.value)}
              placeholder='my-account-prod'
            />
          </FormField>
        </SpaceBetween>
      </form>
    </Modal>
  );
};
